// Small shared pieces of the account / administration sections of the settings page.
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { SessionInfo } from '../../../../shared/types';
import { formatDateTime } from '../../lib/format';
import { accountErrorText } from '../../components/AccountForms';
import { Icon } from '../../components/Icon';

export interface Loaded<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
  /** Replace the shown data (after a change whose result the server returned). */
  set: (data: T) => void;
}

/** Loads `fn()` on mount and on reload(); keeps the previous data while reloading. */
export function useLoaded<T>(fn: () => Promise<T>, fallback: string): Loaded<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fnRef = useRef(fn);
  useEffect(() => {
    fnRef.current = fn;
  });
  const round = useRef(0);
  const reload = useCallback(() => {
    const mine = ++round.current;
    setLoading(true);
    setError(null);
    fnRef.current().then(
      (value) => {
        if (mine !== round.current) return;
        setData(value);
        setLoading(false);
      },
      (err: unknown) => {
        if (mine !== round.current) return;
        setError(accountErrorText(err, fallback));
        setLoading(false);
      },
    );
  }, [fallback]);
  useEffect(() => {
    reload();
    return () => {
      round.current++;
    };
  }, [reload]);
  return { data, loading, error, reload, set: setData };
}

export function LoadError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="notice notice--error" role="alert">
      <Icon name="alert" size={20} className="notice__icon" />
      <div className="notice__content">
        <p>{message}</p>
        <button type="button" className="btn btn--secondary btn--small" onClick={onRetry}>
          <Icon name="refresh" size={18} /> Poskusi znova
        </button>
      </div>
    </div>
  );
}

export function Loading({ children = 'Nalagam …' }: { children?: ReactNode }) {
  return (
    <p className="muted loading-line">
      <Icon name="spinner" size={18} /> {children}
    </p>
  );
}

/** "Chrome · Windows" from a browser's user agent (the server names web sessions the same way). */
export function sessionKindLabel(s: Pick<SessionInfo, 'kind'>): string {
  return s.kind === 'device' ? 'Aplikacija Android' : 'Spletni brskalnik';
}

/** List of sessions (own or, for administrators, another user's). */
export function SessionList({
  sessions,
  onRevoke,
  busyId,
}: {
  sessions: SessionInfo[];
  onRevoke?: (s: SessionInfo) => void;
  busyId?: string | null;
}) {
  if (sessions.length === 0) return <p className="empty">Ni aktivnih prijav.</p>;
  return (
    <ul className="session-list">
      {sessions.map((s) => (
        <li key={s.id} className={`session${s.current ? ' session--current' : ''}`}>
          <span className="session__icon" aria-hidden="true">
            <Icon name={s.kind === 'device' ? 'phone' : 'monitor'} size={22} />
          </span>
          <span className="session__main">
            <span className="session__title">
              {s.deviceName || sessionKindLabel(s)}
              {s.current && <span className="chip chip--done">Ta naprava</span>}
            </span>
            <span className="session__meta">
              {sessionKindLabel(s)} · nazadnje aktivna {formatDateTime(s.lastSeenAt)}
            </span>
            <span className="session__meta">
              Prijava {formatDateTime(s.createdAt)}
              {s.ip ? ` · IP ${s.ip}` : ''}
            </span>
          </span>
          {onRevoke && !s.current && (
            <button
              type="button"
              className="btn btn--secondary btn--small"
              onClick={() => onRevoke(s)}
              disabled={busyId === s.id}
              aria-label={`Odjavi: ${s.deviceName || sessionKindLabel(s)}`}
            >
              <Icon name={busyId === s.id ? 'spinner' : 'logout'} size={18} /> Odjavi
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}
